import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SignInButton } from '@clerk/react';
import { RecommendationBadge } from '@/components/recommendation-badge';
import { ConfidenceBar } from '@/components/ui/confidence-bar';
import { PlatformBadge } from '@/components/platform-badge';
import { ArrowRight, Target, AlertTriangle, Sparkles } from 'lucide-react';

interface DemoAnalysis {
  marketQuestion: string;
  platform: 'polymarket' | 'kalshi';
  recommendation: 'YES' | 'NO';
  confidence: number;
  riskLevel: string;
  yesPrice: number;
  noPrice: number;
  edgeSummary: string;
  reasoning: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  analysis: DemoAnalysis;
}

export function DemoAnalysisModal({ open, onClose, analysis }: Props) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Sample AI Analysis
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Market header */}
          <div>
            <div className="mb-2">
              <PlatformBadge platform={analysis.platform} />
            </div>
            <h3 className="font-semibold text-base">{analysis.marketQuestion}</h3>
            <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
              <span>YES: <strong className="text-foreground">{Math.round(analysis.yesPrice * 100)}¢</strong></span>
              <span>NO: <strong className="text-foreground">{Math.round(analysis.noPrice * 100)}¢</strong></span>
            </div>
          </div>

          {/* Main result */}
          <Card className="border-2 border-primary/20">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Recommendation</span>
                <RecommendationBadge recommendation={analysis.recommendation} size="lg" />
              </div>

              <div>
                <ConfidenceBar confidence={analysis.confidence} />
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Risk Level</span>
                <span className={`font-medium ${
                  analysis.riskLevel === 'Low' ? 'text-emerald-600' :
                  analysis.riskLevel === 'Medium' ? 'text-amber-600' :
                  'text-red-600'
                }`}>{analysis.riskLevel}</span>
              </div>

              <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
                <div className="flex items-center gap-2 mb-1.5">
                  <Target className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold">Edge Summary</span>
                </div>
                <p className="text-sm">{analysis.edgeSummary}</p>
              </div>

              <div>
                <p className="text-sm font-semibold mb-1.5">Claude's Reasoning</p>
                <p className="text-sm text-muted-foreground">{analysis.reasoning}</p>
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-amber-900">Not financial advice. For educational purposes only.</p>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center space-y-2 pt-2">
            <p className="text-sm text-muted-foreground">
              Sign up free to run analyses on any live market
            </p>
            <SignInButton mode="modal">
              <Button className="w-full" size="lg" onClick={onClose}>
                Get Started Free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </SignInButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
